// ─── I soldi del mese ────────────────────────────────────────────────────────
//
// 💶⭐⭐ **LA PRIMA COSA DA SAPERE È COSA QUESTA SCHERMATA NON È.**
//    Non è una cassa: è **quanto vale il lavoro di un mese**, a carico di chi ha
//    giocato. 🗣️ *«Vede gli importi, non incassa»* ⇒ da qui non si incassa e
//    non si cambia un prezzo.
// 🔄 **Dal 27/09/2026 (voce 18) però si VEDE cosa è entrato in cassa**:
//    🗣️ *«ci deve essere una differenza tra l'incassato e quello ancora da
//    incassare»*. È una lettura dei posti in campo, non un saldo del maestro —
//    incassa solo la Segreteria, e la riga in fondo lo dice.
// 🆕 **E in fondo le tariffe del circolo (voce 19d)**, solo da leggere:
//    🗣️ *«l'app Maestri è solamente una visualizzazione di quello che c'è
//    scritto sul gestionale»*.
//
// ⭐ **La riga che lo dichiara sta in TESTA, non in fondo**: arrivando dopo i
//    tre numeri, la si leggerebbe dopo averli già scambiati per un incasso.
//
// ⭐⭐ **E i numeri sono giusti solo grazie alla tappa ④a.** Il dovuto di una
//    volta è la somma degli importi dei giocatori — **assenti compresi, perché
//    la riga c'è**. Prima che l'assenza fosse una spunta, segnare un'assenza
//    voleva dire togliere la persona, cioè far calare questo totale in
//    silenzio. ⇒ *«Chi manca conta lo stesso»* non è una frase gentile in
//    fondo alla schermata: è la cosa che il modello dati adesso garantisce.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  DURATA_DEL_TIPO,
  ETICHETTE_LIVELLO,
  etichettaDeiGiorni,
  formatCents,
  prezzoDellaVoce,
  vociDelTipo,
  type IlCampoDelMese,
  type SoldiDelMese,
  type TariffaLezione,
  type TariffeDelMaestro,
  type TipoTariffa,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { inItaliano, mesePerEsteso, oggi, piuMesi } from '../../lib/time.js';

export function Soldi(): React.JSX.Element {
  const [mese, setMese] = useState(() => oggi().slice(0, 7));

  const soldi = useQuery({
    queryKey: ['maestro', 'soldi', mese],
    queryFn: () => chiama<SoldiDelMese>('GET', `/maestro/soldi?mese=${mese}`),
    /* 🗓️⭐ **IL MESE DI PRIMA RESTA A SCHERMO MENTRE ARRIVA QUELLO NUOVO.**
       Senza, ogni freccia butterebbe via tutta la schermata e rimetterebbe
       «Sto caricando…»: su un telefono a bordo campo è un lampo a ogni tocco,
       e chi sfoglia tre mesi lo vede tre volte.
       ⚖️ E il prezzo di questa scelta è dichiarato: per un istante i numeri
       sono quelli del mese prima. ⇒ Per questo il nome del mese si legge da
       `s.mese`, cioè **dalla risposta**, e non dallo stato: l'etichetta e le
       cifre restano d'accordo sempre, anche in mezzo al giro.
       📌 *Un'intestazione che cambia prima dei numeri che intesta è il modo
          più semplice di far leggere la cifra sbagliata.* */
    placeholderData: (prima) => prima,
  });

  return (
    <>
      <p>
        <Link className="indietro" to="/maestro">
          ‹ La mia agenda
        </Link>
      </p>
      <div className="capo-scheda">
        <h2>I soldi</h2>
      </div>

      {soldi.isLoading ? (
        <Attesa cosa="i soldi del mese" />
      ) : soldi.error ? (
        <Guasto errore={soldi.error} />
      ) : (
        <Dentro s={soldi.data!} vaiA={setMese} inArrivo={soldi.isFetching} />
      )}
    </>
  );
}

function Dentro({
  s,
  vaiA,
  inArrivo,
}: {
  s: SoldiDelMese;
  vaiA: (m: string) => void;
  inArrivo: boolean;
}): React.JSX.Element {
  /* ⛔ Mai avuto niente è una cosa DIVERSA da «questo mese zero», e si dice con
     parole diverse: uno zero qui direbbe «non hai lavorato» a chi non ha ancora
     cominciato. 📌 *Uno zero che vuol dire «non ho dati» è una bugia in cifre.* */
  if (s.primo === null) {
    return (
      <section className="riquadro">
        <p>
          <strong>Non c’è ancora niente da contare.</strong>
        </p>
        <p className="tenue">Qui comparirà quanto vale il tuo lavoro, mese per mese.</p>
      </section>
    );
  }

  const primo = s.mese <= s.primo;
  const ultimo = s.ultimo !== null && s.mese >= s.ultimo;

  return (
    <>
      <p className="capo-righe">
        A carico <strong>e incassato</strong>.
      </p>

      {/* 🎓 Le tariffe stanno in fondo, perché la domanda di chi apre è «quanto
          ho fatto questo mese»: un salto in cima le rende trovabili senza
          spingere giù i numeri. */}
      <button
        type="button"
        className="salto"
        onClick={() =>
          document.getElementById('tariffe')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      >
        Le tariffe del circolo ↓
      </button>

      {/* 🗓️⭐ LE FRECCE VANNO DI MESE IN MESE, e non saltano i mesi vuoti.
          ⚠️ Il mockup salta al mese successivo **che ha qualcosa**, perché nel
          prototipo un mese fuori dai suoi dati risponderebbe «0,00 €» e quello
          zero vorrebbe dire «qui non so». Qui i dati ci sono: uno zero fra il
          primo e l'ultimo mese è uno zero **vero** — quel mese non hai
          lavorato. ⇒ Saltando, la freccia che dice «Il mese prima» porterebbe a
          un mese che non è quello prima.
          📌 *Una scorciatoia nata per coprire un vuoto di dati diventa una
             bugia appena i dati ci sono.* */}
      {/* ⛔ Le frecce si spengono anche MENTRE il mese arriva, e non è
          decorazione: il passo si calcola da `s.mese`, cioè dalla risposta —
          due tocchi rapidi partirebbero tutti e due dallo stesso mese e il
          secondo non andrebbe da nessuna parte. */}
      <div className="mesi" aria-busy={inArrivo}>
        <button
          type="button"
          disabled={primo || inArrivo}
          aria-label="Il mese prima"
          onClick={() => vaiA(piuMesi(s.mese, -1))}
        >
          ‹
        </button>
        <strong>{mesePerEsteso(s.mese)}</strong>
        <button
          type="button"
          disabled={ultimo || inArrivo}
          aria-label="Il mese dopo"
          onClick={() => vaiA(piuMesi(s.mese, 1))}
        >
          ›
        </button>
      </div>

      {/* 💶 «In tutto» si somma QUI e non arriva dal server: un terzo numero
          mandato da fuori è un terzo numero che un giorno può non tornare con
          gli altri due, e a leggerlo è qualcuno che deve fidarsi. */}
      <section className="riquadro conti">
        <p className="conto">
          <span>Già fatte</span>
          <strong>{formatCents(s.fatteCents)}</strong>
        </p>
        <p className="conto">
          <span>Ancora da fare</span>
          <strong>{formatCents(s.daFareCents)}</strong>
        </p>
        <p className="conto grosso">
          <span>In tutto</span>
          <strong>{formatCents(s.fatteCents + s.daFareCents)}</strong>
        </p>
      </section>

      <InCassa s={s} />

      {s.righe.length === 0 ? (
        <p className="tenue piccolo">In questo mese non hai niente in campo.</p>
      ) : (
        /* 🎾 Voce 9, Q2 (27/09/2026, scelta A): senza una riga sopra, chi guarda
           in fretta leggeva queste cifre come una SECONDA somma da aggiungere ai
           tre conti. Sono le stesse, divise per origine, e lo si dice. */
        <section className="origini">
          <h3>Da dove vengono</h3>
          <p className="tenue piccolo">Le stesse cifre di sopra, divise per lezione e per corso.</p>
          <ul className="righe-soldi">
            {s.righe.map((r) => (
              <li key={r.chiave}>
                <span className="nome">{r.nome}</span>
                <span className="quante tenue piccolo">{quanteVolte(r)}</span>
                <strong className="soldi">{formatCents(r.cents)}</strong>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* 💶⭐⭐ IL SECONDO LIBRO: quello fra il maestro e il circolo (tappa ⑧c).
          🗣️ *«io affitto il campo al maestro […] la differenza va al maestro»* ·
          *«incasso io dagli allievi, non il maestro»*.
          ⛔ Compare SOLO se il circolo ha scritto una tariffa: a zero direbbe
          «ti resta tutto», che è una risposta sbagliata data con dei numeri. */}
      {s.campo !== null && <IlCampo c={s.campo} />}

      {/* 🆕 26/09/2026 (R6): le lezioni della sera disdette
          a meno di 48 ore, o dove l'allievo non è venuto. Una riga per volta,
          col perché: una cifra tolta senza la sua ragione è una lite. */}
      {s.trattenute.length > 0 && (
        <section>
          <h3>🌙 Trattenute delle lezioni della sera</h3>
          <ul className="righe-soldi">
            {s.trattenute.map((t) => (
              <li key={t.id}>
                <span className="nome">
                  Lezione del {t.date.slice(8, 10)}/{t.date.slice(5, 7)} alle {t.time}
                </span>
                <span className="quante tenue piccolo">
                  {t.reason === 'no_show'
                    ? 'l’allievo non si è presentato'
                    : `disdetta ${t.hoursBefore ?? 0} ore prima`}
                </span>
                <strong className="soldi">− {formatCents(t.amountCents)}</strong>
              </li>
            ))}
          </ul>
          <p className="regola">
            Di sera, una lezione disdetta a meno di 48 ore o saltata dall’allievo costa 30 € dal tuo compenso.
            Se il motivo era il campo o il circolo, dillo alla Segreteria: la toglie.
          </p>
        </section>
      )}

      <LeTariffe />

      <p className="regola">
        Le volte <strong>annullate</strong> non contano: non le deve nessuno.
      </p>
      <p className="regola">
        Chi <strong>manca</strong> conta lo stesso: il posto resta suo.
      </p>
      <p className="regola">
        Qui vedi cosa è <strong>entrato in cassa</strong>, ma incassa solo la <strong>Segreteria</strong>: se
        un allievo vuole pagare, mandalo da lei.
      </p>
    </>
  );
}

/**
 * 💶⭐⭐ **IN CASSA (voce 18): quanto è entrato, e quanto manca.**
 *
 * ⭐ La barra è il colpo d'occhio, le due cifre la risposta. Sotto il da
 * incassare c'è la parte sulle volte **già fatte**, che è quella da cercare per
 * prima: una lezione di domani non pagata è normale, una di ieri no.
 * ⚠️ I due numeri si contano posto per posto (⇒ `soldiDelMese`): chi ha pagato
 *    di più non copre chi non ha pagato. Per questo incassato + da incassare fa
 *    «In tutto» solo quando nessuno ha pagato più del dovuto.
 */
function InCassa({ s }: { s: SoldiDelMese }): React.JSX.Element {
  const inTutto = s.fatteCents + s.daFareCents;
  const quota = inTutto <= 0 ? 0 : Math.min(100, Math.round((s.incassatoCents / inTutto) * 100));
  return (
    <section className="riquadro conti cassa">
      <p className="tariffa-detta">In cassa, su {formatCents(inTutto)} a carico</p>
      <div className="barra" role="img" aria-label={`Incassato il ${quota} per cento`}>
        <span style={{ width: `${quota}%` }} />
      </div>
      <p className="conto incassato">
        <span>Incassato</span>
        <strong>{formatCents(s.incassatoCents)}</strong>
      </p>
      <p className="conto manca">
        <span>
          Da incassare
          <small>di cui {formatCents(s.daIncassareFatteCents)} sulle volte già fatte</small>
        </span>
        <strong>{formatCents(s.daIncassareCents)}</strong>
      </p>
    </section>
  );
}

/**
 * 🎓 **LE TARIFFE DEL CIRCOLO (voce 19d) — solo da leggere.**
 *
 * ⛔ Una casella non decisa si scrive «non ancora decisa», mai 0 €: zero è un
 *    prezzo, e direbbe «gratis».
 * ⚖️ Ha la sua domanda al server e il suo stato di attesa: se le tariffe non
 *    arrivano, i soldi del mese restano leggibili lo stesso.
 */
function LeTariffe(): React.JSX.Element {
  const t = useQuery({
    queryKey: ['maestro', 'tariffe'],
    queryFn: () => chiama<TariffeDelMaestro>('GET', '/maestro/tariffe'),
  });
  return (
    <section id="tariffe" className="tariffe-circolo">
      <div className="capo-scheda">
        <h3>Le tariffe del circolo</h3>
        <span className="pastiglia">solo da leggere</span>
      </div>
      {t.isLoading ? (
        <Attesa cosa="le tariffe" />
      ) : t.error ? (
        <Guasto errore={t.error} />
      ) : (
        <>
          <TabellaDelTipo kind="lezione" titolo="Lezioni" tariffe={t.data!.lezioni} />
          <TabellaDelTipo kind="allenamento" titolo="Allenamenti" tariffe={t.data!.lezioni} />
          <TabellaDelTipo kind="corso" titolo="Corsi" tariffe={t.data!.lezioni} />
          <TabellaDelTipo kind="tattica" titolo="Partita tattica" tariffe={t.data!.lezioni} />
          <IlCampoAParole campo={t.data!.campo} />
          <p className="regola">
            Le tariffe le decide la <strong>Segreteria</strong> nel gestionale. Qui si leggono soltanto: il
            prezzo di una lezione lo calcola il gestionale dal tipo e dalla durata.
          </p>
        </>
      )}
    </section>
  );
}

function TabellaDelTipo({
  kind,
  titolo,
  tariffe,
}: {
  kind: TipoTariffa;
  titolo: string;
  tariffe: readonly TariffaLezione[];
}): React.JSX.Element {
  const come = kind === 'corso' ? 'al mese, a persona' : `${durataAParole(DURATA_DEL_TIPO[kind])}, a testa`;
  /* 🆕 20/10/2026: della partita tattica il gestionale manda solo il prezzo di questo maestro;
     se non c'è, la riga resta «non ancora decisa». */
  const voci =
    kind === 'tattica'
      ? vociDelTipo(kind, [tariffe.find((x) => x.kind === 'tattica')?.instructorId ?? ''])
      : vociDelTipo(kind);
  return (
    <div className="riquadro tariffe">
      <h4>{titolo}</h4>
      <p className="come">{come}</p>
      <dl>
        {voci.map((v) => {
          const chi =
            v.level !== null
              ? ETICHETTE_LIVELLO[v.level]
              : v.people === 1
                ? '1 persona'
                : `${v.people} persone`;
          const c = prezzoDellaVoce(tariffe, v);
          return <Riga key={chi} chi={chi} cents={c} />;
        })}
      </dl>
    </div>
  );
}

function IlCampoAParole({ campo }: { campo: TariffeDelMaestro['campo'] }): React.JSX.Element {
  if (campo === null) {
    return (
      <div className="riquadro tariffe">
        <h4>Il campo</h4>
        <p className="come">Il circolo non ha ancora scritto il prezzo del campo.</p>
      </div>
    );
  }
  return (
    <div className="riquadro tariffe">
      <h4>Il campo</h4>
      <p className="come">
        all’ora, in vigore dal {inItaliano(campo.validFrom)}
        {campo.validTo !== null && ` al ${inItaliano(campo.validTo)}`}
      </p>
      <dl>
        {campo.gruppi.flatMap((g) => {
          const giorni = etichettaDeiGiorni(g.giorni);
          if (g.pezzi.length === 0) return [<Riga key={giorni} chi={giorni} cents={null} />];
          return g.pezzi.map((p) => (
            <Riga
              key={`${giorni}-${p.dalle}`}
              chi={`${giorni} · ${senzaZero(p.dalle)}–${senzaZero(p.alle)}`}
              cents={p.centsPerHour}
            />
          ));
        })}
      </dl>
    </div>
  );
}

function Riga({ chi, cents }: { chi: string; cents: number | null }): React.JSX.Element {
  return (
    <>
      <dt>{chi}</dt>
      {cents === null ? <dd className="vuota">non ancora decisa</dd> : <dd>{formatCents(cents)}</dd>}
    </>
  );
}

/** `60` → «1 ora», `90` → «1 ora e mezza», `120` → «2 ore». */
function durataAParole(minuti: number): string {
  const ore = Math.floor(minuti / 60);
  const mezza = minuti % 60 === 30;
  const base = ore === 1 ? '1 ora' : `${ore} ore`;
  if (minuti % 60 === 0) return base;
  return mezza ? `${base} e mezza` : `${minuti} minuti`;
}

/** «08:00» → «8:00»: si legge come lo si dice. */
function senzaZero(ora: string): string {
  return ora.replace(/^0(\d)/, '$1');
}

/**
 * 💶⭐⭐ **IL CAMPO, cioè quello che il circolo TRATTIENE — e quanto resta.**
 *
 * 🗣️ Il verso è suo, ed è l'unica cosa che questo riquadro deve dire bene:
 * *«incasso io dagli allievi, non il maestro»* ⇒ gli allievi pagano **al
 * circolo**, il circolo si tiene l'ora di campo e la differenza è del maestro.
 * **Il maestro non versa niente a nessuno**, e la riga sotto il riquadro lo dice
 * con quelle parole — perché «− 360,00 €» letto da solo somiglia a un debito.
 *
 * ⭐ **Le due parole in alto sono le STESSE del riquadro sopra** — «Già fatte» e
 * «Ancora da fare» — ed è deliberato: il taglio fra passato e futuro è **lo
 * stesso** nei due libri, e due parole diverse per lo stesso confine farebbero
 * sospettare due confini. 🔄 La terza invece cambia apposta: sopra è «In tutto»,
 * qui è **«Ti restano»**, perché è l'unico numero della schermata su cui
 * qualcuno deve fare qualcosa.
 */
function IlCampo({ c }: { c: IlCampoDelMese }): React.JSX.Element {
  const allievi = c.allieviFatteCents + c.allieviDaFareCents;
  const campo = c.campoFatteCents + c.campoDaFareCents;
  /* 🔗 La cifra che LEGA questa schermata a quella della segreteria: là si
     contano solo le volte già fatte, qui tutto il mese. Due numeri veri che,
     senza questa riga, sembrano smentirsi. */
  const giàMaturato = c.allieviFatteCents - c.campoFatteCents;

  return (
    <>
      <p className="capo-righe">
        E questo è <strong>il campo</strong>, che il circolo trattiene.
      </p>

      <section className="riquadro conti">
        {/* 🧾 La cifra all'ora si NOMINA solo quando nel mese ne è valsa una
            sola. Con due sarebbe vera per metà mese, e chi controlla
            moltiplicando le ore non ritroverebbe il totale — concludendo che a
            sbagliare è la schermata. */}
        {c.tariffaUnica !== null && (
          <p className="tariffa-detta">
            {formatCents(c.tariffaUnica.centsPerHour)} all’ora, dal {inItaliano(c.tariffaUnica.validFrom)}.
          </p>
        )}

        {/* 👁️⭐ LE ORE STANNO SOTTO L'ETICHETTA, non accanto alla cifra, e la
            cifra non va a capo. A 320 px «− » e «200,00 €» finivano su due
            righe: una sottrazione spezzata si legge come una somma, e
            1.080 + 360 fa un numero che qui non vuol dire niente.
            📏 Misurato allora: ciascuna delle due cure basta da sola — il
               difetto torna solo togliendole tutt'e due. */}
        <p className="conto meno">
          <span>
            Già fatte<small>{ore(c.minutiFatte)} ore</small>
          </span>
          <strong>− {formatCents(c.campoFatteCents)}</strong>
        </p>
        <p className="conto meno">
          <span>
            Ancora da fare<small>{ore(c.minutiDaFare)} ore</small>
          </span>
          <strong>− {formatCents(c.campoDaFareCents)}</strong>
        </p>
        <p className="conto netto">
          <span>
            Ti restano
            <small>
              {formatCents(allievi)} meno {formatCents(campo)} di campo
            </small>
          </span>
          <strong>{formatCents(allievi - campo)}</strong>
        </p>
      </section>

      <p className="regola">
        Gli allievi pagano <strong>al circolo</strong>. Il campo il circolo se lo <strong>trattiene</strong>:
        quello che resta è tuo, e non devi versare niente.
      </p>
      <p className="regola">
        Di questi, <strong>{formatCents(giàMaturato)}</strong> sono sulle volte <strong>già fatte</strong>: è
        la cifra che vede la segreteria. Il resto sono volte che devi ancora fare.
      </p>
      {c.tariffaUnica === null && (
        <p className="regola">
          In questo mese il campo è costato <strong>a prezzi diversi</strong> (fasce orarie o listini
          diversi): le ore per una cifra sola non rifanno il totale, che è la somma volta per volta.
        </p>
      )}
      {c.scoperte > 0 && (
        <p className="regola">
          <strong>
            {c.scoperte} {c.scoperte === 1 ? 'volta è' : 'volte sono'} fuori da questo conto
          </strong>
          : il listino del campo non ha un prezzo per {c.scoperte === 1 ? 'quell’ora' : 'quelle ore'}, e{' '}
          {c.scoperte === 1 ? 'non porta' : 'non portano'} né il proprio incasso né il proprio costo. Il
          circolo può completarlo.
        </p>
      )}
    </>
  );
}

/**
 * Le ore in DECIMALI — `8`, `7,5` — perché sono quelle che si moltiplicano per
 * la tariffa. ⚖️ `oreEminuti` di `Agenda.tsx` rende `1h30`, che si legge ma non
 * si moltiplica: sono due numeri per due domande diverse, non un doppione.
 */
function ore(minuti: number): string {
  return (minuti / 60).toLocaleString('it-IT', { maximumFractionDigits: 1 });
}

/**
 * «4 volte · 1 annullata», oppure «1 annullata» e basta.
 *
 * 👁️ **Il caso senza il quale si legge «0 volte · 1 annullata».** Un mese in cui
 * l'unica volta è stata annullata esiste davvero — sul banco è novembre — e
 * quella frase mette in fila due modi di dire la stessa cosa, uno dei quali è
 * uno zero. ⇒ Quando di volte non ne resta nessuna, si dice **solo** cosa è
 * stato annullato.
 * 📌 *Uno zero accanto al numero che lo spiega non aggiunge niente: ripete.*
 */
function quanteVolte(r: { volte: number; annullate: number }): string {
  const annullate =
    r.annullate === 0 ? '' : `${r.annullate} ${r.annullate === 1 ? 'annullata' : 'annullate'}`;
  if (r.volte === 0) return annullate === '' ? 'niente' : annullate;
  const volte = r.volte === 1 ? '1 volta' : `${r.volte} volte`;
  return annullate === '' ? volte : `${volte} · ${annullate}`;
}
